import { Children, Component } from 'react';
import { debounceInAnimationFrame } from '@optimus-web/common';

import Measurer from '../Measurer/Measurer';

import { getDefaultItemSize, getDirectionDependentAttributes } from './handlers';
import {
    VirtualContainerElement,
    VirtualScrollContainerElement,
    VirtualSizerElement,
    VirtualViewportElement,
} from './style';
import { Alignment, Child, DirectionDependentAttributes, VirtualDirection, VirtualProps, VirtualState } from './types';
import { calculateViewportProps, createChildrenArray } from './utils';

const INITIAL_STATE: VirtualState = { offset: 0 };

export default class Virtual extends Component<VirtualProps, VirtualState> {
    static defaultProps: Partial<VirtualProps> = {
        overscanSize: 200,
        defaultItemWidth: 48,
        defaultItemHeight: 48,
        direction: VirtualDirection.Vertical,
        shouldScrollToIndex: true,
    };

    readonly state: VirtualState = INITIAL_STATE;

    private sizeMap = new Map<React.Key, number>();
    private childrenArray: Child[] = [];
    private scrollableSize = 0;

    private scrollContainerElement?: HTMLDivElement;
    private containerElement?: HTMLDivElement;

    private visibleChildren?: Child[];

    private dimension: DirectionDependentAttributes['dimension'];
    private scrollDirection: DirectionDependentAttributes['scrollDirection'];
    private defaultItemSize: number;

    constructor(props: VirtualProps) {
        super(props);
        const { children, direction } = this.props;
        const { dimension, scrollDirection } = getDirectionDependentAttributes(direction);
        this.dimension = dimension;
        this.scrollDirection = scrollDirection;
        this.childrenArray = createChildrenArray(children);
        this.defaultItemSize = getDefaultItemSize(direction, props);
        this.scrollableSize = this.childrenArray.length * this.defaultItemSize;
    }

    componentDidMount() {
        const { currentIndex, scrollToAlignment } = this.props;

        if (currentIndex) {
            this.scrollToIndex(currentIndex, scrollToAlignment);
        }

        // We rely on refs and measurements of children so we need one more render after the initial one
        this.forceUpdate();
    }

    UNSAFE_componentWillReceiveProps(nextProps: VirtualProps) {
        // THIS ALLOWS CHILDREN TO ACTUALLY GET UPDATED
        // TODO use keys to just copy new children
        this.childrenArray = createChildrenArray(nextProps.children);

        // TODO Use keys to perform proper check of children change
        const childrenCount = Children.count(this.props.children);
        const nextChildrenCount = Children.count(nextProps.children);

        if (childrenCount !== nextChildrenCount) {
            // NOTE - clear only if new key appears ?? - is it needed at all?
            this.sizeMap.clear();
            this.scrollableSize = this.childrenArray.length * this.defaultItemSize;

            if (this.props.onScrollableSizeChange) {
                this.props.onScrollableSizeChange(this.scrollableSize);
            }

            if (this.scrollContainerElement) {
                this.scrollContainerElement[this.scrollDirection] = 0;
            }

            this.setState(INITIAL_STATE);
        }

        if (
            typeof nextProps.currentIndex !== 'undefined' &&
            this.props.currentIndex !== nextProps.currentIndex &&
            this.props.shouldScrollToIndex
        ) {
            this.scrollToIndex(nextProps.currentIndex, nextProps.scrollToAlignment);
        }
    }

    setOffset = (offset: number) => this.setState({ offset });

    componentDidUpdate(__: VirtualProps, prevState: VirtualState) {
        if (this.scrollContainerElement) {
            const { offset, scrollReason } = this.state;

            if (prevState.offset !== offset && scrollReason === 'requested') {
                let offsetWasFixed;
                const { currentIndex, scrollToAlignment } = this.props;

                if (currentIndex) {
                    // Because measurement happens only after initial render, we need might need to recalculate the scroll position
                    const fixedOffset = this.getOffsetForIndex(currentIndex, scrollToAlignment);

                    if (fixedOffset !== offset) {
                        offsetWasFixed = true;
                        this.setOffset(fixedOffset);
                    }
                }

                if (!offsetWasFixed) {
                    // Synchronize requested scrolling with actual scroll container. Actual scroll event will be ignored.
                    this.scrollContainerElement[this.scrollDirection] = offset;
                }
            }
        }
    }

    render() {
        const { start, end } = this.getVisibleRange();
        const { startIndex, endIndex, paddingSize } = calculateViewportProps(this.childrenArray, this.sizeMap, {
            startPosition: start,
            endPosition: end,
            defaultItemSize: this.defaultItemSize,
        });
        const { direction, onScrollableSizeChange, ...other } = this.props;

        this.visibleChildren = this.childrenArray.slice(startIndex, endIndex + 1);

        const translate = direction === VirtualDirection.Horizontal ? 'translateX' : 'translateY';

        return (
            <VirtualContainerElement ref={this.handleContainerRef} {...other}>
                <VirtualScrollContainerElement
                    direction={direction}
                    ref={this.handleScrollContainerRef}
                    onScroll={this.handleScroll}
                    data-test-scroll-container
                >
                    <VirtualSizerElement direction={direction} scrollableSize={this.scrollableSize} />
                    <VirtualViewportElement
                        direction={direction}
                        style={{ transform: `${translate}(${paddingSize}px)` }}
                    >
                        {this.renderChildren(this.visibleChildren)}
                    </VirtualViewportElement>
                </VirtualScrollContainerElement>
            </VirtualContainerElement>
        );
    }

    private getVisibleRange = () => {
        if (!(this.containerElement && this.scrollContainerElement)) {
            return { start: 0, end: 0 };
        }

        // FIXME: type assertion is required by tsc until fixed, see https://github.com/DefinitelyTyped/DefinitelyTyped/issues/11640
        const overscanSize = this.props.overscanSize as number;
        const { offset } = this.state;

        const containerSize: number =
            this.props.containerSize || this.containerElement.getBoundingClientRect()[this.dimension];
        const start = Math.max(0, offset - overscanSize);
        const end = Math.min(offset + containerSize + overscanSize, this.scrollableSize);

        return { start, end };
    };

    private getOffsetForIndex = (index: number, alignment = Alignment.Start) => {
        const boundedIndex = Math.max(0, Math.min(index, this.childrenArray.length - 1));

        let offset = 0;

        for (let i = 0; i < boundedIndex; ++i) {
            const child = this.childrenArray[i];
            const size = this.getSize(child.key);
            offset += size;
        }

        if (this.containerElement) {
            const containerSize =
                this.props.containerSize || this.containerElement.getBoundingClientRect()[this.dimension];
            const indexSize = this.getSize(this.childrenArray[boundedIndex].key);

            if (alignment === Alignment.Center) {
                const alignmentShift = (containerSize - indexSize) / 2;
                offset -= alignmentShift;
            }
            if (alignment === Alignment.End) {
                const alignmentShift = containerSize - indexSize;
                offset -= alignmentShift;
            }
        }

        // TODO use container size to shorten upper limit
        const boundedOffset = Math.max(0, Math.min(offset, this.scrollableSize));

        return boundedOffset;
    };

    private scrollToIndex = (index: number, alignment = Alignment.Start) => {
        const offset = this.getOffsetForIndex(index, alignment);
        this.setState({ offset, scrollReason: 'requested' });
    };

    private getSize = (key: React.Key) => {
        const measuredSize = this.sizeMap.get(key);
        if (typeof measuredSize === 'number') {
            return measuredSize;
        }

        return this.defaultItemSize;
    };
    private debouncedForcedUpdate = debounceInAnimationFrame(() => this.forceUpdate());
    private handleSizeChange = (identifier: React.Key, dimensions: { height: number; width: number }) => {
        const size = dimensions[this.dimension];
        const oldSize = this.getSize(identifier);
        const sizeDiff = size - oldSize;

        if (sizeDiff) {
            this.scrollableSize += sizeDiff;
            this.sizeMap.set(identifier, size);
            this.debouncedForcedUpdate();
            if (this.props.onScrollableSizeChange) {
                this.props.onScrollableSizeChange(this.scrollableSize);
            }
        }
    };

    private handleScroll = () => {
        if (!this.scrollContainerElement) {
            return;
        }

        const offset = this.scrollContainerElement[this.scrollDirection];

        if (offset < 0 || offset === this.state.offset) {
            return;
        }

        this.setState({ offset, scrollReason: 'observed' });
    };

    private handleContainerRef = (c: HTMLDivElement) => (this.containerElement = c);
    private handleScrollContainerRef = (c: HTMLDivElement) => (this.scrollContainerElement = c);

    private renderChildren = (children: Child[]) =>
        children.map(({ key, child }) => (
            <Measurer key={key} identifier={key} onSizeChange={this.handleSizeChange}>
                {child}
            </Measurer>
        ));
}
